import { Injectable, inject } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormControl,
} from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import type { PlanBreakUpCalculationResponse, StaffDiscountCalculationRequest, StaffDiscountSignUpChargeRequest } from '@fitness-force/models';
import { CommonService, PlansubscriptionService, TimeconversionService, ToastMessageService } from '@fitness-force/api';
import type { GetClientSubscriptionResponse } from '@fitness-force/models';

export interface ProcessedCharge {
  id: number;
  name_primary_language: string;
  original_amount: number;
  editable_amount: number;
  amount_in_minor_currency: number;
  discount_amt_in_minor_currency: number;
  taxable_cost_in_minor_currency: number;
  tax: unknown;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class FreezeChargeService {
  private commonService = inject(CommonService);
  private plansubscriptionService = inject(PlansubscriptionService);
  private timeService = inject(TimeconversionService);

  /**
   * Process the charge breakup response and return processed charges
   */
  processChargeBreakup(
    response: PlanBreakUpCalculationResponse,
    existingCharges: ProcessedCharge[]
  ): ProcessedCharge[] {
    if (
      response?.plan_breakup_calculation?.charging_today?.signup_charges
    ) {
      return response.plan_breakup_calculation.charging_today.signup_charges.map(
        (charge) => {
          const existingCharge = existingCharges.find(
            (c) => c['id'] === (charge as any)['id']
          );
          return {
            ...charge,
            original_amount:
              existingCharge?.original_amount ??
              (charge as any)['amount_in_minor_currency'],
            editable_amount: this.commonService.ConvertAmountFromMinorCurrency(
              (charge as any)['taxable_cost_in_minor_currency'] as number
            ),
            amount_in_minor_currency: (charge as any)['taxable_cost_in_minor_currency'],
          } as ProcessedCharge;
        }
      );
    }
    return [];
  }

  /**
   * Setup FormArray controls for each charge
   */
  setupChargeAmountFormControls(
    charges: ProcessedCharge[],
    chargeAmountsArray: UntypedFormArray,
    chargeDiscountsArray: UntypedFormArray
  ): void {
    chargeAmountsArray.clear();
    chargeDiscountsArray.clear();

    charges.forEach((charge) => {
      const amountControl = new UntypedFormControl(charge.editable_amount);
      chargeAmountsArray.push(amountControl);

      const originalAmount = this.commonService.ConvertAmountFromMinorCurrency(
        charge.original_amount
      );
      const discountPercentage =
        charge.discount_amt_in_minor_currency && originalAmount > 0
          ? (charge.discount_amt_in_minor_currency / charge.original_amount) *
            100
          : 0;
      chargeDiscountsArray.push(new UntypedFormControl(discountPercentage));
    });
  }

  /**
   * Calculate total charge amount from signup charges
   */
  calculateTotalChargeAmount(charges: ProcessedCharge[]): number {
    return charges.reduce((total, charge) => {
      return total + (charge.amount_in_minor_currency as number);
    }, 0);
  }

  /**
   * Validate and apply charge discount when amount field changes
   * Returns updated charges array (immutable) or null if invalid
   */
  applyChargeAmountChange(
    chargeIndex: number,
    charges: ProcessedCharge[],
    chargeAmountsArray: UntypedFormArray,
    chargeDiscountsArray: UntypedFormArray,
    singleStaffDiscount: number
  ): { charges: ProcessedCharge[]; valid: boolean } {
    if (chargeIndex < 0 || chargeIndex >= charges.length) {
      return { charges, valid: false };
    }

    const charge = charges[chargeIndex];
    const lineItemChargeAmount =
      this.commonService.ConvertAmountFromMinorCurrency(charge.original_amount);

    const amtControl = chargeAmountsArray.at(chargeIndex);
    const disControl = chargeDiscountsArray.at(chargeIndex);

    if (!amtControl || !disControl) return { charges, valid: false };

    const newAmount = amtControl.value;
    const minAllowedAmount =
      lineItemChargeAmount -
      (lineItemChargeAmount * singleStaffDiscount) / 100;

    if (newAmount >= minAllowedAmount && newAmount <= lineItemChargeAmount) {
      const discountPercentage =
        lineItemChargeAmount > 0
          ? 100 - (newAmount * 100) / lineItemChargeAmount
          : 0;
      disControl.setValue(discountPercentage);

      const updatedCharges = [...charges];
      updatedCharges[chargeIndex] = {
        ...charge,
        editable_amount: newAmount,
        amount_in_minor_currency:
          this.commonService.ConvertMinorCurrencyFromAmount(newAmount),
        discount_amt_in_minor_currency:
          charge.original_amount -
          this.commonService.ConvertMinorCurrencyFromAmount(newAmount),
      };
      return { charges: updatedCharges, valid: true };
    } else {
      setTimeout(() => {
        amtControl.setValue(lineItemChargeAmount);
        disControl.setValue(0);
      }, 10);
      return { charges, valid: false };
    }
  }

  /**
   * Validate and apply charge amount from discount percentage
   * Returns updated charges array (immutable) or null if invalid
   */
  applyChargeDiscountChange(
    chargeIndex: number,
    charges: ProcessedCharge[],
    chargeAmountsArray: UntypedFormArray,
    chargeDiscountsArray: UntypedFormArray,
    singleStaffDiscount: number
  ): { charges: ProcessedCharge[]; valid: boolean } {
    if (chargeIndex < 0 || chargeIndex >= charges.length) {
      return { charges, valid: false };
    }

    const charge = charges[chargeIndex];
    const lineItemChargeAmount =
      this.commonService.ConvertAmountFromMinorCurrency(charge.original_amount);

    const amtControl = chargeAmountsArray.at(chargeIndex);
    const disControl = chargeDiscountsArray.at(chargeIndex);

    if (!amtControl || !disControl) return { charges, valid: false };

    const discountPercentage = disControl.value;

    if (discountPercentage < 0 || discountPercentage > singleStaffDiscount) {
      setTimeout(() => {
        amtControl.setValue(lineItemChargeAmount);
        disControl.setValue(0);
      }, 10);
      return { charges, valid: false };
    }

    const netValue =
      lineItemChargeAmount -
      (lineItemChargeAmount * discountPercentage) / 100;
    const minAllowedAmount =
      lineItemChargeAmount -
      (lineItemChargeAmount * singleStaffDiscount) / 100;

    if (netValue >= minAllowedAmount) {
      amtControl.setValue(netValue);

      const updatedCharges = [...charges];
      updatedCharges[chargeIndex] = {
        ...charge,
        editable_amount: netValue,
        amount_in_minor_currency:
          this.commonService.ConvertMinorCurrencyFromAmount(netValue),
        discount_amt_in_minor_currency:
          charge.original_amount -
          this.commonService.ConvertMinorCurrencyFromAmount(netValue),
      };
      return { charges: updatedCharges, valid: true };
    } else {
      setTimeout(() => {
        amtControl.setValue(lineItemChargeAmount);
        disControl.setValue(0);
      }, 10);
      return { charges, valid: false };
    }
  }

  /**
   * Reset all charges to their original amounts
   */
  resetChargesToOriginal(
    charges: ProcessedCharge[],
    chargeAmountsArray: UntypedFormArray,
    chargeDiscountsArray: UntypedFormArray
  ): ProcessedCharge[] {
    return charges.map((charge, index) => {
      const originalAmount =
        this.commonService.ConvertAmountFromMinorCurrency(
          charge.original_amount
        );
      chargeAmountsArray.at(index)?.setValue(originalAmount);
      chargeDiscountsArray.at(index)?.setValue(0);

      return {
        ...charge,
        editable_amount: originalAmount,
        amount_in_minor_currency: charge.original_amount,
        discount_amt_in_minor_currency: 0,
      };
    });
  }

  /**
   * Build staff discount calculation request
   */
  buildStaffDiscountRequest(
    charges: ProcessedCharge[],
    clientId: number,
    subscription: GetClientSubscriptionResponse,
    timeService: TimeconversionService
  ): StaffDiscountCalculationRequest {
    const staffDiscountSignUpCharges: StaffDiscountSignUpChargeRequest[] =
      charges.map((charge) => ({
        id: charge['id'] as number,
        discount_amt_in_minor_currency:
          (charge.discount_amt_in_minor_currency as number) || 0,
      }));

    return {
      client_id: clientId,
      issue_date: timeService.ConvertLocalDateTimeToUTC(new Date()),
      subscription_bill_cycle_day: subscription.bill_cycle_day || 1,
      subscription_start_date:
        typeof subscription.start_date === 'string'
          ? subscription.start_date
          : new Date().toISOString(),
      service_id: subscription.id ? [subscription.id] : [0],
      charging_today: {
        service_first_charge: { discount_amt_in_minor_currency: 0 },
        service_non_recurring: { discount_amt_in_minor_currency: 0 },
        signup_charges: staffDiscountSignUpCharges,
        deposit: [],
        products: [],
      },
      charging_recurring: {
        service_recurring: { discount_amt_in_minor_currency: 0 },
      },
    };
  }

  /**
   * Get tax amount from tax line items
   */
  getTaxAmount(tax: Array<{ amt_in_minor_currency: number }>): number {
    if (!tax) return 0;
    return tax.reduce((acc, obj) => acc + obj.amt_in_minor_currency, 0);
  }

  /**
   * Get per-charge tax breakdown lines for display
   */
  getChargeTaxBreakdown(
    charge: { tax: Array<{ id: number; type: string; amt_in_minor_currency: number; rate: number }> | null }
  ): Array<{ type: string; amt_in_minor_currency: number; rate: number }> {
    if (!charge.tax || charge.tax.length === 0) return [];
    return charge.tax.map((t) => ({
      type: t.type,
      amt_in_minor_currency: t.amt_in_minor_currency,
      rate: t.rate,
    }));
  }

  /**
   * Get total charge tax amount across all charges
   */
  getTotalChargeTaxAmount(
    charges: Array<{ tax: Array<{ amt_in_minor_currency: number }> | null }>
  ): number {
    let taxAmount = 0;
    charges.forEach((charge) => {
      if (charge.tax != null) {
        taxAmount += charge.tax.reduce(
          (acc: number, obj: { amt_in_minor_currency: number }) =>
            acc + obj.amt_in_minor_currency,
          0
        );
      }
    });
    return taxAmount;
  }

  /**
   * Get charge net cost (with or without tax based on isInclusive)
   */
  getChargeNetCost(
    charge: { taxable_cost_in_minor_currency: number; tax: Array<{ amt_in_minor_currency: number }> },
    isInclusive: boolean
  ): number {
    return isInclusive
      ? charge.taxable_cost_in_minor_currency + this.getTaxAmount(charge.tax)
      : charge.taxable_cost_in_minor_currency;
  }

  /**
   * Get total one-time cost including all charges and taxes
   */
  getTotalOneTimeCost(charges: ProcessedCharge[]): number {
    if (!charges || charges.length === 0) return 0.00000001;

    const chargeCost = charges.reduce(
      (acc, obj) => acc + (obj['taxable_cost_in_minor_currency'] as number),
      0
    );
    const chargeTaxCost = this.getTotalChargeTaxAmount(
      charges as Array<{ tax: Array<{ amt_in_minor_currency: number }> | null }>
    );
    const total = chargeCost + chargeTaxCost;
    return total === 0 ? 0.00000001 : total;
  }
}
